import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  ThemeProvider,
  createMuiTheme,
  experimentalStyled as styled
} from '@material-ui/core/styles';
import {
  Box,
  Card,
  Container,
  Hidden,
  Link,
  Typography
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../utils/axiosConfig';
import { PATH_AUTH } from '../../routes/paths';
import Page from '../../components/Page';
import { FullLogo } from '../../components/Logo';
import { LoginForm } from '../../components/authentication/login';
// import './fonts/IBMPlexMono/IBMPlexMono.ttf';

const loginTitleFont = createMuiTheme({
  typography: {
    fontFamily: ['IBM Plex Mono', 'monospace'].join(',')
  }
});

// ----------------------------------------------------------------------

const RootStyle = styled(Page)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex'
  }
}));

const HeaderStyle = styled('header')(({ theme }) => ({
  top: 0,
  zIndex: 9,
  lineHeight: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  padding: theme.spacing(3),
  justifyContent: 'space-between',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    padding: theme.spacing(7, 5, 0, 7)
  }
}));

const SectionStyle = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 464,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  margin: theme.spacing(2, 0, 2, 2)
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0)
}));

// ----------------------------------------------------------------------

export default function Login() {
  const { t: translate } = useTranslation('login');
  const { customerid } = useParams<{ customerid?: string }>();

  const [srcImage, setSrcImage] = useState<string>(
    '/static/illustrations/Vector-11.svg'
  );

  useEffect(() => {
    if (customerid) {
      testSrc(`/static/illustrations/illustration_${customerid}.png`);
    }
  }, [customerid]);

  const testSrc = (path: string) => {
    axiosInstance
      .get(path)
      .then(() => setSrcImage(path))
      .catch(() => {});
  };

  const removeCookies = () => {
    document.cookie.split(';').forEach((c) => {
      document.cookie = c
        .replace(/^ +/, '')
        .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
    });
  };

  useEffect(() => {
    removeCookies();
  }, []);

  return (
    <RootStyle
      title={`${translate('login:login_title')} | ${translate(
        'global:company'
      )}`}
    >
      <HeaderStyle>
        <RouterLink to="/">
          <FullLogo />
        </RouterLink>
        {/* <Hidden smDown>
          <Typography
            variant="body2"
            sx={{
              mt: { md: -2 }
            }}
          >
            {translate('login:noaccountyet')} &nbsp;
            <Link
              underline="none"
              variant="subtitle2"
              component={RouterLink}
              to={PATH_AUTH.register}
            >
              {translate('login:getstarted')}
            </Link>
          </Typography>
        </Hidden> */}
      </HeaderStyle>

      <Hidden mdDown>
        <SectionStyle>
          <Box sx={{ position: 'relative' }}>
            <ThemeProvider theme={loginTitleFont}>
              <Typography
                variant="body1"
                fontWeight={700}
                fontSize="1.5rem"
                sx={{
                  px: 5,
                  mt: 15,
                  mb: 15,
                  pl: 4.5,
                  pr: 4.5,
                  fontFamily: 'IBM Plex Mono, monospace',
                  position: 'absolute',
                  left: 0,
                  top: 2.5
                }}
              >
                Business Intelligence & Marketing Suite
              </Typography>
            </ThemeProvider>
            <Box sx={{ padding: '2rem .5rem', opacity: 0.1 }}>
              <img src={srcImage} alt="Login image" />
            </Box>
          </Box>
        </SectionStyle>
      </Hidden>

      <Container maxWidth="sm">
        <ContentStyle>
          <Box sx={{ mb: 5, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" gutterBottom>
                {translate('login:login_title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {translate('login:login_text')}
              </Typography>
            </Box>
          </Box>

          <LoginForm />

          <Hidden smUp>
            <Typography variant="body2" align="center" sx={{ mt: 3 }}>
              {translate('login:noaccountyet')}&nbsp;
              <Link
                variant="subtitle2"
                component={RouterLink}
                to={PATH_AUTH.register}
              >
                {translate('login:getstarted')}
              </Link>
            </Typography>
          </Hidden>
        </ContentStyle>
      </Container>
    </RootStyle>
  );
}
